"use client";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupIcon from "@mui/icons-material/Group";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import { useRememberedGuestInfo } from "@/hooks/useRememberedGuestInfo";
import type { BookingIntent } from "@/types/booking-intent";
import { totalGuestsFromForm } from "@/utils/partySize";

export type GuestContactValues = {
    fullName: string;
    phoneNumber: string;
    email: string;
    note: string;
};

const schema = Yup.object().shape({
    fullName: Yup.string().required("Required"),
    phoneNumber: Yup.string()
        .required("Required")
        .matches(/^[0-9]+$/, "Must be a number")
        .length(10, "Must be 10 digits"),
    email: Yup.string().required("Required").email("Must be a valid email"),
    note: Yup.string().max(500, "Must be 500 characters or less"),
});

type Props = {
    intent: BookingIntent;
    onBack: () => void;
    onComplete: (values: GuestContactValues) => void;
    initialValues?: GuestContactValues;
};

export const AIContactForm = ({ intent, onBack, onComplete, initialValues }: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const {
        rememberedInfo,
        isLoaded: isRememberedInfoLoaded,
        saveRememberedInfo,
        clearRememberedInfo,
    } = useRememberedGuestInfo();

    const formik = useFormik<GuestContactValues>({
        initialValues: initialValues ?? {
            fullName: "",
            phoneNumber: "",
            email: "",
            note: intent.note ?? "",
        },
        validationSchema: schema,
        onSubmit: (values) => {
            if (rememberMe) {
                saveRememberedInfo({
                    fullName: values.fullName,
                    phoneNumber: values.phoneNumber,
                    email: values.email,
                });
            } else if (rememberedInfo) {
                clearRememberedInfo();
            }
            onComplete(values);
        },
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setValues,
    } = formik;

    // Pre-fill from a previously "remembered" booking, but only on first arrival at this step
    // (initialValues is defined when restoring values on back-navigation from SUMMARY). All three
    // fields are set in one setValues call so a single validation pass runs against the final
    // values, instead of racing async validations against each other.
    useEffect(() => {
        if (initialValues !== undefined) return;
        if (!isRememberedInfoLoaded || !rememberedInfo) return;
        setValues((prev) => ({
            ...prev,
            fullName: rememberedInfo.fullName,
            phoneNumber: rememberedInfo.phoneNumber,
            email: rememberedInfo.email,
        }));
        setRememberMe(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRememberedInfoLoaded, rememberedInfo]);

    const guestCount = totalGuestsFromForm(intent);
    const dateLabel = intent.date ? dayjs(intent.date).format("ddd, MMM D") : "";
    const timeLabel = intent.time ?? "";

    return (
        <Box sx={{ maxWidth: 520, mx: "auto", width: "100%" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{ mb: 3, textTransform: "none", color: "text.secondary" }}
            >
                Back
            </Button>

            {/* Confirmation banner */}
            <Box
                sx={{
                    mb: 4,
                    p: 2.5,
                    borderRadius: "16px",
                    bgcolor: "rgba(16, 185, 129, 0.06)",
                    border: "1px solid rgba(16, 185, 129, 0.2)",
                }}
            >
                <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
                    <CheckCircleOutlineIcon sx={{ color: "#10B981", fontSize: 22, mt: 0.2 }} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#065F46" }}>
                        Booking slot confirmed!
                    </Typography>
                </Stack>

                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, pl: 0.5 }}>
                    Your date, time, and party size are all set. Now add your contact details to
                    complete the booking.
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                        icon={<EventAvailableIcon sx={{ fontSize: 15 }} />}
                        label={`${dateLabel} @ ${timeLabel}${intent.areaName ? ` · ${intent.areaName}` : ""}`}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            bgcolor: "rgba(16, 185, 129, 0.12)",
                            color: "#065F46",
                            "& .MuiChip-icon": { color: "#10B981" },
                        }}
                    />
                    <Chip
                        icon={<GroupIcon sx={{ fontSize: 15 }} />}
                        label={`${guestCount} ${guestCount === 1 ? "guest" : "guests"}`}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            bgcolor: "rgba(16, 185, 129, 0.12)",
                            color: "#065F46",
                            "& .MuiChip-icon": { color: "#10B981" },
                        }}
                    />
                </Stack>
            </Box>

            <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                Your details
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                    <TextField
                        fullWidth
                        required
                        label="Full name"
                        name="fullName"
                        value={values.fullName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.fullName && errors.fullName)}
                        helperText={touched.fullName && errors.fullName}
                        autoComplete="name"
                    />
                    <TextField
                        fullWidth
                        required
                        label="Phone number"
                        name="phoneNumber"
                        type="tel"
                        value={values.phoneNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(
                            (touched.phoneNumber || !!values.phoneNumber) && errors.phoneNumber,
                        )}
                        helperText={
                            (touched.phoneNumber || !!values.phoneNumber) && errors.phoneNumber
                        }
                        autoComplete="tel"
                    />
                    <TextField
                        fullWidth
                        required
                        label="Email"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                        autoComplete="email"
                    />
                    <TextField
                        fullWidth
                        label="Note (optional)"
                        name="note"
                        multiline
                        rows={3}
                        value={values.note}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.note && errors.note)}
                        helperText={touched.note && errors.note}
                        placeholder="Got any special requests? Leave it here!"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1">
                                    Remember my details on this device
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    We&apos;ll pre-fill your contact details next time you book here
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: "flex-start" }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={isSubmitting}
                        sx={{ mt: 1 }}
                    >
                        {isSubmitting ? <CircularProgress size={22} /> : "Continue"}
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
};
